import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Box, Heading, HStack, IconButton, Spinner, Text, Tooltip, VStack } from "@chakra-ui/react";
import { HiOutlineCode } from "react-icons/hi";
import { relativeTime } from "../placing/PlacingSummaryParts";
import { errorFromResponse } from "../../utils/apiErrors";

const WORKFLOW_API = "https://workflow.poc.mrc.ai/api/temporal/activities";
const SEEN_KEY = "mrc.home.tasksSeen";
const STATUS_COLOURS = { new: "gray", creating: "purple", running: "green", in_progress: "blue", paused: "orange", completed: "green", failed: "red" };

const readSeen = () => {
  try {
    return window.localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
};
const writeSeen = (value) => {
  try {
    window.localStorage.setItem(SEEN_KEY, value);
  } catch {
    // "new" lasts for this visit only
  }
};
const time = (v) => new Date(v?.$date ?? v ?? 0).getTime();

/**
 * The user's open workflow tasks (not completed), newest first. A task is
 * NEW if it arrived since the last visit to this page. refreshKey reloads it.
 */
const WorkflowTasks = ({ refreshKey, onError }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState(null);
  // The last visit, read once; this visit then becomes the last one
  const lastVisit = useRef(null);
  if (lastVisit.current === null) {
    lastVisit.current = readSeen() || new Date().toISOString();
    writeSeen(new Date().toISOString());
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${WORKFLOW_API}/headers`);
        if (!res.ok) throw await errorFromResponse(res, `${res.status} ${res.statusText}`);
        const data = await res.json();
        if (!cancelled) setTasks((Array.isArray(data) ? data : []).filter((t) => String(t.status || "").toLowerCase() !== "completed"));
      } catch (err) {
        if (!cancelled) {
          setTasks((prev) => prev || []);
          onError?.(err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // onError is a fresh function each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const openPayload = async (task) => {
    let payload = task.payload;
    if (payload === undefined) {
      try {
        const res = await fetch(`${WORKFLOW_API}/${task._id}`);
        payload = res.ok ? (await res.json())?.payload : undefined;
      } catch {
        payload = undefined;
      }
    }
    navigate(`/workflow/${task._id}/payload`, { state: { jsonString: JSON.stringify(payload ?? {}, null, 2) } });
  };

  const sorted = [...(tasks || [])].sort((a, b) => time(b.createdDate) - time(a.createdDate));
  const isNew = (t) => time(t.createdDate) > time(lastVisit.current);

  return (
    <Box borderWidth="1px" borderRadius="md" bg="whiteAlpha.800" px={4} py={3}>
      <HStack justify="space-between" mb={2}>
        <Heading size="sm">Your tasks</Heading>
        {tasks && <Badge borderRadius="full" px={2}>{tasks.length}</Badge>}
      </HStack>
      {tasks === null ? (
        <Spinner size="sm" />
      ) : sorted.length === 0 ? (
        <Text fontSize="sm" color="gray.500">No open tasks.</Text>
      ) : (
        <VStack align="stretch" spacing={0} divider={<Box borderBottomWidth="1px" />} maxH="420px" overflowY="auto">
          {sorted.map((t) => (
            <HStack key={t._id} py={1.5} spacing={3} align="start" cursor="pointer" _hover={{ bg: "blackAlpha.50" }}
              onClick={() => navigate(`/workflow/${t._id}`)}>
              <Box flex="1" minW={0}>
                <HStack spacing={2}>
                  <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{t.name || "Task"}</Text>
                  {isNew(t) && <Badge colorScheme="red" variant="solid" borderRadius="full" fontSize="0.6em">NEW</Badge>}
                </HStack>
                <Text fontSize="xs" color="gray.500" noOfLines={1}>{t.type || "—"} · {relativeTime(t.createdDate)}</Text>
              </Box>
              <Badge colorScheme={STATUS_COLOURS[String(t.status || "").toLowerCase()] || "gray"} textTransform="none" flexShrink={0}>
                {t.status || "—"}
              </Badge>
              <Tooltip label="View payload" hasArrow>
                <IconButton size="xs" variant="ghost" aria-label="View payload" icon={<HiOutlineCode />}
                  onClick={(e) => { e.stopPropagation(); openPayload(t); }} />
              </Tooltip>
            </HStack>
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default WorkflowTasks;
